import { apiFailure } from '@/lib/apiFailure'
import { waitWords } from '@/lib/retryWait'
import { api } from '@/util/api'
import type { ChatCardKind, ChatPerson, WatchApiFailure } from './types'

const path = encodeURIComponent

/**
 * A stage's guest link as its hosts see it (`GuestLinkPayload`): the link to copy, when it lapses and how many more
 * people can use it. The token rides in the fragment (`/invite#<token>`), never a path or query string.
 */
export interface GuestLink {
  id: string
  url: string
  /** ISO 8601. */
  expires_at: string
  uses_left: number
}

/** What a host's Invite on stage answers: the person is now an invited guest. */
export interface GuestInvitation {
  user_id: string
  state: 'invited'
}

/**
 * Someone a host invited on stage who hasn't joined (`GET /stages/:id/guest_invitations`): their seat waits for them.
 * `invited_at` (ISO 8601) is when they started waiting: invited, or back from a dropped connection. A nudge doesn't move
 * it.
 */
export interface InvitedGuest {
  user: ChatPerson
  invited_at: string
}

export type { ChatCardKind, ChatPerson } from './types'

/**
 * A chat card (docs/plans/2026-10-19-watch-invites.md, Chat cards). Its body is empty: the client words it from its
 * kind and subject. `invite` is posted by the inviting host about the guest; `joined` and `left` by the guest about
 * themselves.
 */
export interface ChatCard {
  id?: string
  kind: ChatCardKind
  user: ChatPerson
  subject: ChatPerson
  created_at: string
}

/**
 * Why an invite call failed. The API's own types first (`already_on_stage`, `stage_full`, `chat_blocked`, `forbidden`,
 * `not_found`), then what the status says without one: `unauthorized` (signed out), `rate_limited` (429), `network`
 * (no answer) and `unknown`.
 */
export type InviteErrorType =
  | 'already_on_stage'
  | 'stage_full'
  | 'chat_blocked'
  | 'forbidden'
  | 'not_found'
  | 'unauthorized'
  | 'rate_limited'
  | 'network'
  | 'unknown'

const API_TYPES = new Set<InviteErrorType>(['already_on_stage', 'stage_full', 'chat_blocked', 'forbidden', 'not_found', 'rate_limited'])

const WORDS: Record<InviteErrorType, string> = {
  already_on_stage: 'They’re on stage already.',
  stage_full: 'Your stage is full.',
  chat_blocked: 'They can’t chat here right now.',
  forbidden: 'Only the stage’s hosts can do that.',
  not_found: 'They’re no longer here.',
  unauthorized: 'Sign in to do that.',
  rate_limited: 'That’s a lot of tries. Try again shortly.',
  network: 'We couldn’t reach StreamBridge. Check your connection and try again.',
  unknown: 'Something went wrong. Try again.',
}

/**
 * A failed invite call, typed. `message` is ready to show (a toast's title, say): short, and with the wait when the
 * API throttled. `serverMessage` is the API's own text, for logs.
 */
export class InviteError extends Error {
  readonly type: InviteErrorType
  readonly status: number | null
  readonly retryAfterMs: number | null
  readonly serverMessage: string | null

  constructor(type: InviteErrorType, failure: Pick<WatchApiFailure, 'status' | 'message' | 'retryAfterMs'>) {
    const wait = type === 'rate_limited' ? waitWords(failure.retryAfterMs) : null
    super(wait ? `That’s a lot of tries. Try again in ${wait}.` : WORDS[type])
    this.name = 'InviteError'
    this.type = type
    this.status = failure.status
    this.retryAfterMs = failure.retryAfterMs
    this.serverMessage = failure.message
  }
}

/**
 * Reads a rejected call as an `InviteError`: the body's `type` when it is one of ours, else from the status. Rails'
 * app-wide 403 says `unauthorized`; any 403 here is `forbidden`.
 */
export function inviteError(error: unknown): InviteError {
  if (error instanceof InviteError) return error
  const failure = apiFailure(error)
  const named = failure.type as InviteErrorType | null
  const type: InviteErrorType =
    named && API_TYPES.has(named) ? named
      : failure.status === null ? 'network'
        : failure.status === 401 ? 'unauthorized'
          : failure.status === 403 ? 'forbidden'
            : failure.status === 404 ? 'not_found'
              : failure.status === 429 ? 'rate_limited'
                : 'unknown'
  return new InviteError(type, failure)
}

/** Runs a call and rethrows its failure as an `InviteError`. */
async function typed<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request()
  } catch (error) {
    throw inviteError(error)
  }
}

type InvitedListener = (stageId: string) => void
const invitedListeners = new Set<InvitedListener>()

/**
 * Hears which stage's invited list changed: an Invite on stage went through, so "Invite to your stage" reads its
 * Waiting list again. Returns the unsubscribe.
 */
export function onInvitedChange(listener: InvitedListener): () => void {
  invitedListeners.add(listener)
  return () => {
    invitedListeners.delete(listener)
  }
}

function invitedChanged(stageId: string) {
  for (const listener of [...invitedListeners]) {
    try {
      listener(stageId)
    } catch (error) {
      // One listener's fault doesn't undo the invite, or keep the others from hearing it.
      console.error(error)
    }
  }
}

/** A host invites someone on stage (from their chat message): they become an invited guest and an invite card posts. */
export async function inviteGuest(stageId: string, userId: string): Promise<GuestInvitation> {
  const body = await typed(() => api.post<{ data: GuestInvitation }>(`/stages/${path(stageId)}/guest_invitations`, { user_id: userId }))
  invitedChanged(stageId)
  return body.data
}

/** Who is invited on the stage and hasn't joined, oldest invitation first (hosts only). Reads have their own budget. */
export function invited(stageId: string): Promise<InvitedGuest[]> {
  return typed(() => api.get<{ data: { invited: InvitedGuest[] } }>(`/stages/${path(stageId)}/guest_invitations`)).then((body) => body.data.invited)
}

/**
 * A host takes back an invitation still waiting, quietly (no card). `not_found` when it isn't waiting any more: they
 * joined (a live guest comes off with `removeGuest`), declined, or it lapsed.
 */
export function cancelInvitation(stageId: string, userId: string): Promise<void> {
  return typed(() => api.delete<void>(`/stages/${path(stageId)}/guest_invitations/${path(userId)}`)).then(() => undefined)
}

/** The invited person says Not now. Quiet (no card), and fine to repeat. */
export function declineInvite(stageId: string): Promise<void> {
  return typed(() => api.delete<void>(`/stages/${path(stageId)}/guest_invitations/mine`)).then(() => undefined)
}

/** The stage's live guest link, made if there is none (hosts only), so the invite panel can show it again. */
export function getStageLink(stageId: string): Promise<GuestLink> {
  return typed(() => api.get<{ data: GuestLink }>(`/stages/${path(stageId)}/link`)).then((body) => body.data)
}

/** A new guest link (hosts only). The old one stops working. */
export function rotateStageLink(stageId: string): Promise<GuestLink> {
  return typed(() => api.post<{ data: GuestLink }>(`/stages/${path(stageId)}/link`)).then((body) => body.data)
}

/** A host takes a guest off the stage: their seat frees and their connection is lowered (hosts can't be removed here). */
export function removeGuest(stageId: string, userId: string): Promise<void> {
  return typed(() => api.delete<void>(`/stages/${path(stageId)}/members/${path(userId)}`)).then(() => undefined)
}

/**
 * The invite calls as one object. Components call through it, so a story can stand in for a call with
 * `spyOn(invitesApi, 'getStageLink')`.
 */
export const invitesApi = { inviteGuest, declineInvite, getStageLink, rotateStageLink, removeGuest, invited, cancelInvitation }
